import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, Plus, Trash2, Loader2, X } from "lucide-react";
import TopBar from "@/components/TopBar";
import TickerSearchDialog from "@/components/TickerSearchDialog";
import DisclaimerFooter from "@/components/DisclaimerFooter";
import { api } from "@/lib/api";

function fmt(v, d = 2) { return v == null ? "—" : Number(v).toFixed(d); }
function SignalChip({ s }) {
  const cls = s === "LONG" ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" :
              s === "SHORT" ? "text-red-300 border-red-500/40 bg-red-500/10" :
              "text-slate-400 border-slate-600 bg-slate-500/5";
  return <span className={`mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${cls}`}>{s || "—"}</span>;
}

export default function WatchlistsPage() {
  const [openSearch, setOpenSearch] = useState(false);
  const [lists, setLists] = useState([]);
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [tickerInput, setTickerInput] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const loadLists = async () => {
    const r = await api.get("/signal/watchlists");
    setLists(r.data.watchlists || []);
    if (!selected && r.data.watchlists?.[0]) setSelected(r.data.watchlists[0].id);
  };
  const loadSummary = async (id) => {
    if (!id) { setSummary(null); return; }
    setLoading(true);
    try {
      const r = await api.get(`/signal/watchlists/${id}/summary`);
      setSummary(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadLists(); }, []);
  useEffect(() => { if (selected) loadSummary(selected); }, [selected]);

  const createList = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/signal/watchlists", { name: newName.trim(), tickers: [] });
      setNewName(""); await loadLists(); setSelected(r.data.id);
    } finally { setBusy(false); }
  };

  const deleteList = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/signal/watchlists/${id}`);
      if (selected === id) setSelected(null);
      await loadLists();
    } finally { setBusy(false); }
  };

  const addTicker = async () => {
    if (!tickerInput.trim() || !selected) return;
    setBusy(true);
    try {
      await api.post(`/signal/watchlists/${selected}/tickers`, { ticker: tickerInput.trim().toUpperCase() });
      setTickerInput("");
      await Promise.all([loadSummary(selected), loadLists()]);
    } catch (e) {
      // surface error briefly via alert; keep it simple
      alert(e.response?.data?.detail || e.message || "Failed to add ticker");
    } finally { setBusy(false); }
  };

  const removeTicker = async (t) => {
    if (!selected) return;
    await api.delete(`/signal/watchlists/${selected}/tickers/${t}`);
    await Promise.all([loadSummary(selected), loadLists()]);
  };

  return (
    <div className="min-h-screen bg-[#080B11] flex flex-col">
      <TopBar onOpenSearch={() => setOpenSearch(true)} />
      <main className="flex-1 pb-14">
        <div className="max-w-[1400px] mx-auto p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <Bookmark className="h-4 w-4 text-cyan-400" />
            <h1 className="mono text-lg font-bold text-slate-100 tracking-tight uppercase">Watchlists</h1>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-3">
            {/* Left: list of watchlists */}
            <div className="border border-[#1E293B] bg-[#0F1626]">
              <div className="px-3 py-2 border-b border-[#1E293B] bg-[#0B111C]">
                <span className="mono text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Your Lists</span>
              </div>
              <div className="p-2 flex gap-2">
                <input
                  data-testid="watchlist-name-input"
                  value={newName} onChange={(e) => setNewName(e.target.value)}
                  placeholder="New list name…"
                  className="mono text-xs flex-1 bg-[#0B111C] border border-[#1E293B] px-2 py-1 text-slate-100 focus:outline-none focus:border-cyan-500/60"
                />
                <button data-testid="watchlist-create" onClick={createList} disabled={busy} className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-200 flex items-center gap-1">
                  <Plus className="h-3 w-3" /> Add
                </button>
              </div>
              <div className="border-t border-[#1E293B]">
                {lists.length === 0 && (<div className="p-3 mono text-[11px] text-slate-500 text-center">No lists yet.</div>)}
                {lists.map((w) => (
                  <div
                    key={w.id}
                    data-testid={`watchlist-item-${w.id}`}
                    onClick={() => setSelected(w.id)}
                    className={`flex items-center justify-between px-3 py-2 border-b border-[#151C2C] cursor-pointer hover:bg-[#151C2C] ${selected === w.id ? "bg-[#172036]" : ""}`}
                  >
                    <div className="min-w-0">
                      <div className="mono text-xs text-slate-100 truncate">{w.name}</div>
                      <div className="mono text-[10px] text-slate-500 uppercase tracking-widest">{(w.tickers || []).length} tickers</div>
                    </div>
                    <button
                      data-testid={`watchlist-delete-${w.id}`}
                      onClick={(e) => { e.stopPropagation(); deleteList(w.id); }}
                      className="text-slate-500 hover:text-red-300"
                    ><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: summary */}
            <div className="border border-[#1E293B] bg-[#0F1626]">
              <div className="px-3 py-2 border-b border-[#1E293B] bg-[#0B111C] flex items-center gap-2">
                <span className="mono text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                  {summary?.name || "Select a list"}
                </span>
                {selected && (
                  <div className="ml-auto flex gap-2">
                    <input
                      data-testid="watchlist-ticker-input"
                      value={tickerInput} onChange={(e) => setTickerInput(e.target.value)}
                      placeholder="Add ticker…"
                      className="mono text-xs bg-[#0B111C] border border-[#1E293B] px-2 py-1 text-slate-100 focus:outline-none focus:border-cyan-500/60 w-32"
                    />
                    <button data-testid="watchlist-add-ticker" onClick={addTicker} disabled={busy} className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-200 flex items-center gap-1">
                      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Add
                    </button>
                  </div>
                )}
              </div>
              <div className="p-2">
                {loading && (<div className="p-6 text-slate-500 mono text-xs text-center flex items-center justify-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>)}
                {!loading && summary && summary.rows.length === 0 && (<div className="p-6 mono text-[11px] text-slate-500 text-center">This list is empty. Add a ticker above.</div>)}
                {!loading && summary && summary.rows.length > 0 && (
                  <table className="q-table w-full">
                    <thead><tr><th>Ticker</th><th>Signal</th><th>Prev</th><th className="right">Score</th><th>Band</th><th className="right">Price</th><th className="right">Entry</th><th className="right">Stop</th><th className="right">T1</th><th>Regime</th><th></th></tr></thead>
                    <tbody>
                      {summary.rows.map((r) => (
                        <tr
                          key={r.ticker}
                          data-testid={`watchlist-row-${r.ticker}`}
                          className="hover:bg-[#151C2C] cursor-pointer"
                          onClick={() => nav(`/stock/${r.ticker}`)}
                        >
                          <td className="text-slate-100 font-bold">
                            {r.ticker}
                            {r.signal_changed && (
                              <span data-testid={`watchlist-changed-${r.ticker}`} className="ml-2 mono text-[9px] uppercase tracking-widest px-1 py-0.5 border border-cyan-500/60 text-cyan-300 bg-cyan-500/10">
                                {r.prev_signal || "?"} → {r.signal}
                              </span>
                            )}
                          </td>
                          <td><SignalChip s={r.signal} /></td>
                          <td><SignalChip s={r.prev_signal} /></td>
                          <td className={"right " + (r.signal === "LONG" ? "text-emerald-300" : r.signal === "SHORT" ? "text-red-300" : "text-slate-400")}>
                            {r.signal === "SHORT" ? fmt(r.short_score, 1) : fmt(r.long_score, 1)}
                          </td>
                          <td className="text-slate-400">{r.strength_band || "—"}</td>
                          <td className="right text-slate-200">{r.price != null ? `$${fmt(r.price)}` : "—"}</td>
                          <td className="right text-slate-200">{r.ideal_entry != null ? `$${fmt(r.ideal_entry)}` : "—"}</td>
                          <td className="right text-red-300">{r.stop_loss != null ? `$${fmt(r.stop_loss)}` : "—"}</td>
                          <td className="right text-emerald-300">{r.t1 != null ? `$${fmt(r.t1)}` : "—"}</td>
                          <td className="text-slate-500 text-[10px]">{r.regime || "—"}</td>
                          <td>
                            <button data-testid={`watchlist-remove-${r.ticker}`} onClick={(e) => { e.stopPropagation(); removeTicker(r.ticker); }} className="text-slate-500 hover:text-red-300">
                              <X className="h-3 w-3" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <TickerSearchDialog open={openSearch} onOpenChange={setOpenSearch} />
      <DisclaimerFooter />
    </div>
  );
}
